import * as React from 'react';

import {Button} from '../../button/button';
// @ts-expect-error
import {CopyIcon} from '../../icon/svg/copy-icon';
import {Popover} from '../../popover/popover';
import {useRefObserver} from '../../utils/hooks/use-ref-observer';

type Props = {
    target?: React.Ref<any> | null | undefined;
    contentToCopy?: string;
    size: 'big' | 'xlarge';
    variant?: 'primary' | 'secondary';
    text?: {
        button: string;
        tooltip: string;
        copiedButton: string;
        copiedTooltip: string;
    };
    icon?: React.ComponentType<any>;
    onCopy?: () => void;
};

const textDefaults = {
    button: 'Copy',
    tooltip: 'Copy',
    copiedButton: 'Copied',
    copiedTooltip: 'Copied!',
} as const;

export function CopyButton(props: Props) {
    const [target, setTarget] = React.useState<any>(null);
    const buttonRef = React.useRef(null);
    const {isHovered} = useRefObserver(buttonRef, ['isHovered']);

    const text = {...textDefaults, ...props.text} as const;
    const copyText = text.button;
    const copyTooltipText = text.tooltip;
    const copiedButtonText = text.copiedButton;
    const copiedTooltipText = text.copiedTooltip;

    const [buttonText, setButtonText] = React.useState<string>(copyText);
    const [tooltipText, setTooltipText] = React.useState<string>(copyTooltipText);
    const [showTooltip, setShowTooltip] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (props.target && typeof props.target === 'object') {
            setTarget(props.target.current);
        }
    }, [props.target]);

    const handleCopy = (contentToCopy: string) => {
        navigator.clipboard.writeText(contentToCopy).then(() => {
            setButtonText(copiedButtonText);
            setTooltipText(copiedTooltipText);
            if (props.onCopy) {
                props.onCopy();
            }
        });
    };

    const copyToClipboard = (event: React.SyntheticEvent<any>) => {
        event.stopPropagation();
        if (!target && !props.contentToCopy) return;

        if (props.contentToCopy) {
            handleCopy(props.contentToCopy);

            return;
        }

        if (target) {
            let textToCopy = '';
            if (target.nodeName === 'INPUT' || target.nodeName === 'TEXTAREA') {
                textToCopy = target.value;
            } else {
                textToCopy = target.innerText;
            }

            handleCopy(textToCopy);
        }
    };

    React.useEffect(() => {
        // @ts-expect-error
        let timeoutId;
        if (isHovered) {
            setShowTooltip(true);
        } else if (buttonText === copiedButtonText) {
            timeoutId = setTimeout(() => {
                setShowTooltip(false);
                setButtonText(copyText);
                setTooltipText(copyTooltipText);
            }, 500);
        } else {
            setShowTooltip(false);
        }

        return () => {
            // @ts-expect-error
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isHovered, buttonText, copiedButtonText, copyText, copiedTooltipText, copyTooltipText]);

    return (
        <>
            <Button
                size={props.size}
                icon={props.icon || CopyIcon}
                variant={props.variant}
                getButtonRef={buttonRef}
                onClick={copyToClipboard}
            >
                {buttonText === copiedButtonText ? copiedButtonText : copyText}
            </Button>
            {showTooltip && (
                <Popover anchor={buttonRef.current} bodyColor='dark' location='top'>
                    <div className='pad-8'>{tooltipText}</div>
                </Popover>
            )}
        </>
    );
}
